import {
  findCorrespondingGeoRecord,
  SignalEntry,
  validateSignalsForNewPanel,
} from "@/shared/domain/workspaces/signalValidation";
import { WorkspaceBuilder } from "@/shared/domain/workspaces/WorkspaceBuilder";
import { LayoutNavigationState } from "@/shared/navigation/layoutNavigation";
import { LoggerService } from "@/shared/services";
import { PanelType } from "@/shared/state/visualization";
import { determineDefaultPanelType } from "@/shared/state/visualization/schema/v1";

/**
 * Build the visualizer layout for an event's message-path scope.
 *
 * Paths are grouped into panels:
 * 1. Same-topic plot series of one canonical type share a plot panel.
 * 2. Same-topic log paths share a log panel; likewise raw-message paths a
 *    raw-message panel.
 * 3. Images get one panel per path.
 * 4. A topic's latitude/longitude siblings join into one map panel.
 *
 * Each group is checked with `validateSignalsForNewPanel`; a group that
 * fails is logged and skipped so one bad path doesn't drop the rest of the
 * scope. Returns `undefined` when nothing survives to lay out.
 *
 * Panels are ordered by category — image panels, then shared panels
 * (plot/log/raw-message), then maps — not by where each path appears in the
 * event's scope.
 */
export function buildEventScopeLayout(
  scopedPaths: SignalEntry[],
): LayoutNavigationState | undefined {
  const builder = new WorkspaceBuilder();
  let panelCount = 0;

  groupIntoPanels(scopedPaths).forEach((group) => {
    const validation = validateSignalsForNewPanel(group);
    if (!validation.ok) {
      LoggerService.error(
        `Skipping a panel of the event's scope: ${validation.error}`,
        validation.invalid,
      );
      return;
    }

    // Validation can return more entries than it was given: a lone map
    // coordinate comes back with its pair member bundled in.
    builder.addMultiSignalPanel({
      type: validation.panelType,
      signals: validation.entries.map((entry) => ({
        topicRecord: entry.topic,
        messagePathRecord: entry.messagePath,
      })),
    });
    panelCount += 1;
  });

  if (panelCount === 0) {
    return undefined;
  }
  return builder.build();
}

/**
 * Pre-built panels are an enhancement on top of opening the visualizer, not
 * a precondition for it — anything {@link buildEventScopeLayout} throws is
 * logged and swallowed so the visualizer still opens, just without a
 * layout.
 */
export function tryBuildEventScopeLayout(
  scopedPaths: SignalEntry[],
): LayoutNavigationState | undefined {
  try {
    return buildEventScopeLayout(scopedPaths);
  } catch (err) {
    LoggerService.error("Failed to build a layout for the event's scope", err);
    return undefined;
  }
}

/**
 * Split an event's message paths into one array per panel to create.
 */
function groupIntoPanels(scopedPaths: SignalEntry[]): SignalEntry[][] {
  const singles: SignalEntry[][] = [];
  const shared = new Map<string, SignalEntry[]>();
  const mapsByTopic = new Map<string, SignalEntry[]>();

  scopedPaths.forEach((entry) => {
    const panelType = determineDefaultPanelType(
      entry.messagePath.canonical_data_type,
    );
    switch (panelType) {
      case PanelType.Plot:
        // The canonical type joins the key so a boolean series never lands
        // on a float axis, where it would read poorly.
        appendTo(
          shared,
          `${panelType}:${entry.topic.topic_id}:${entry.messagePath.canonical_data_type}`,
          entry,
        );
        break;
      case PanelType.Log:
      case PanelType.RawMessage:
        appendTo(shared, `${panelType}:${entry.topic.topic_id}`, entry);
        break;
      case PanelType.Map:
        appendTo(mapsByTopic, entry.topic.topic_id, entry);
        break;
      case PanelType.Image:
      default:
        // One panel per path. The default arm covers panel types that
        // determineDefaultPanelType never returns (Document, ThreeD);
        // should one appear, validation rejects its single-entry group.
        singles.push([entry]);
        break;
    }
  });

  return [
    ...singles,
    ...shared.values(),
    ...Array.from(mapsByTopic.values()).flatMap(pairMapEntries),
  ];
}

/**
 * Pair a topic's in-scope coordinates into latitude/longitude groups: a
 * coordinate whose sibling is also in scope groups with it; one whose
 * sibling is not goes alone, and validation bundles the sibling from the
 * topic's records.
 */
function pairMapEntries(entries: SignalEntry[]): SignalEntry[][] {
  const groups: SignalEntry[][] = [];
  const consumed = new Set<string>();

  entries.forEach((entry) => {
    if (consumed.has(entry.messagePath.message_path_id)) {
      return;
    }
    consumed.add(entry.messagePath.message_path_id);

    const sibling = findCorrespondingGeoRecord(entry.topic, entry.messagePath);
    const siblingEntry =
      sibling &&
      entries.find(
        (candidate) =>
          candidate.messagePath.message_path_id === sibling.message_path_id &&
          !consumed.has(candidate.messagePath.message_path_id),
      );

    if (siblingEntry) {
      consumed.add(siblingEntry.messagePath.message_path_id);
      groups.push([entry, siblingEntry]);
    } else {
      groups.push([entry]);
    }
  });

  return groups;
}

function appendTo(
  groups: Map<string, SignalEntry[]>,
  key: string,
  entry: SignalEntry,
): void {
  const group = groups.get(key);
  if (group) {
    group.push(entry);
  } else {
    groups.set(key, [entry]);
  }
}
